import { useCallback } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { AgentOptionItem } from '@Models';

const optionCardVariants = tv({
  slots: {
    root: 'mt-3 mb-1 flex flex-col gap-1',
    button:
      'group/optioncard relative flex w-full items-center gap-3 rounded-lg border bg-[#fbfaf9] px-3 py-2.5 text-left transition-all duration-150 animate-journey-slide-up focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c6cf6]/40 focus-visible:ring-offset-1 active:scale-[.99]',
    number:
      'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md font-mono text-[11px] font-bold tabular-nums tracking-wide transition-colors',
    buttonContent: 'min-w-0 flex-1',
    label: 'text-sm font-semibold leading-snug text-gray-900',
    hint: 'mt-0.5 text-xs leading-snug text-gray-500',
    icon: 'flex-shrink-0 transition-colors',
  },
  variants: {
    state: {
      idle: {
        button: 'border-gray-100 cursor-pointer hover:border-[#bdacfa] hover:bg-[#f5f3ff]',
        number: 'bg-[#ffffff] text-gray-400 group-hover/optioncard:bg-[#ede8fe] group-hover/optioncard:text-[#7c6cf6]',
        icon: 'text-gray-300 group-hover/optioncard:text-[#7c6cf6]',
      },
      selected: {
        button: 'border-[#7c6cf6] bg-[#f5f3ff] cursor-default',
        number: 'bg-[#7c6cf6] text-white',
        icon: 'text-[#7c6cf6]',
      },
      disabled: {
        button: 'border-gray-100 cursor-not-allowed opacity-40',
        number: 'bg-gray-50 text-gray-400',
        icon: 'text-gray-300',
      },
    },
  },
  defaultVariants: { state: 'idle' },
});

interface OptionCardsProps {
  options: AgentOptionItem[];
  selectedId?: string;
  disabled?: boolean;
  onSelect?: (option: AgentOptionItem) => void;
}

const OptionCards = ({ options, selectedId, disabled = false, onSelect }: OptionCardsProps) => {
  const styles = optionCardVariants();

  const stateFor = useCallback(
    (id: string): 'idle' | 'selected' | 'disabled' => {
      if (selectedId === id) return 'selected';
      if (disabled) return 'disabled';
      return 'idle';
    },
    [selectedId, disabled],
  );

  if (!options || options.length === 0) return null;

  return (
    <div className={styles.root()} role="radiogroup" aria-label="Options">
      {options.map((option, idx) => {
        const state = stateFor(option.id);
        const isSelected = state === 'selected';
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={state === 'disabled'}
            onClick={() => !disabled && onSelect?.(option)}
            className={styles.button({ state })}
            style={{ animationDelay: `${idx * 0.08 + 0.05}s` }}>
            <div className={styles.number({ state })} aria-hidden>
              {String(idx + 1).padStart(2, '0')}
            </div>
            <div className={styles.buttonContent()}>
              <div className={styles.label()}>{option.label}</div>
              {option.hint && <div className={styles.hint()}>{option.hint}</div>}
            </div>
            <div className={styles.icon({ state })}>
              <span className="relative inline-block h-4 w-4">
                <ChevronRight
                  className={`absolute inset-0 h-4 w-4 transition-all duration-300 ${
                    isSelected ? 'scale-50 opacity-0' : 'scale-100 opacity-100'
                  }`}
                />
                <Check
                  className={`absolute inset-0 h-4 w-4 transition-all duration-300 ${
                    isSelected ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
                  }`}
                />
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default OptionCards;
